import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Layout from "../../Layout";
import DietService from "../../Services/DietService";
import VitalService from "../../Services/VitalService";
import moment from "moment";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { message, Spin, Empty, DatePicker } from "antd";
import {
  FaUpload,
  FaTimes,
  FaRunning,
  FaClipboardList,
  FaClock,
  FaUtensils,
  FaCamera,
  FaWeight,
  FaTape,
  FaChartPie,
  FaHeartbeat,
  FaChartLine,
  FaRuler,
} from "react-icons/fa";
import { GiBiceps, GiForearm, GiLeg } from "react-icons/gi";
import Webcam from "react-webcam";
import { useRef } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { notifyDietImageUpload } from "../../config/firebase";

dayjs.extend(isSameOrBefore);

function DietPlan() {
  const { loggedUser } = useSelector((state) => state.auth);
  const [dietPlans, setDietPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [currentMeal, setCurrentMeal] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const webcamRef = useRef(null);

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: "environment",
  };

  const getDayNumber = (title) => {
    if (!title) return 0;
    const match = title.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };

  const [isViewImageModalOpen, setIsViewImageModalOpen] = useState(false);
  const [viewImageUrl, setViewImageUrl] = useState("");

  const [isAddVitalsModalOpen, setIsAddVitalsModalOpen] = useState(false);
  const [vitals, setVitals] = useState({
    weight: "-",
    waist: "-",
    hip: "-",
    chest: "-",
    neck: "-",
    biceps: "-",
    forearm: "-",
    calf: "-",
    thighs: "-",
  });
  const [tempVitals, setTempVitals] = useState({
    weight: "",
    waist: "",
    hip: "",
    chest: "",
    neck: "",
    biceps: "",
    forearm: "",
    calf: "",
    thighs: "",
    recordedDate: dayjs(),
  });
  // history of saved vitals entries for trend graphs
  const [vitalsHistory, setVitalsHistory] = useState([
    {
      date: "15 Apr",
      weight: 76,
      waist: 36,
      hip: 40,
      chest: 42,
      neck: 38,
      biceps: 32,
      forearm: 28,
      calf: 36,
      thighs: 56,
    },
    {
      date: "22 Apr",
      weight: 75,
      waist: 35.5,
      hip: 39.5,
      chest: 41.5,
      neck: 37.5,
      biceps: 32,
      forearm: 28,
      calf: 35.5,
      thighs: 55.5,
    },
    {
      date: "29 Apr",
      weight: 74.5,
      waist: 35,
      hip: 39,
      chest: 41,
      neck: 37,
      biceps: 31.5,
      forearm: 27.5,
      calf: 35,
      thighs: 55,
    },
    {
      date: "06 May",
      weight: 74,
      waist: 35,
      hip: 39,
      chest: 41,
      neck: 37,
      biceps: 31.5,
      forearm: 27.5,
      calf: 35,
      thighs: 55,
    },
    {
      date: "10 May",
      weight: 73.5,
      waist: 34.5,
      hip: 38.5,
      chest: 40.5,
      neck: 36.5,
      biceps: 31,
      forearm: 27,
      calf: 34.5,
      thighs: 54.5,
    },
    {
      date: "14 May",
      weight: 73,
      waist: 34.5,
      hip: 38.5,
      chest: 40.5,
      neck: 36.5,
      biceps: 31,
      forearm: 27,
      calf: 34.5,
      thighs: 54.5,
    },
    {
      date: "19 May",
      weight: 72.5,
      waist: 34,
      hip: 38,
      chest: 40,
      neck: 36,
      biceps: 30.5,
      forearm: 26.5,
      calf: 34,
      thighs: 54,
    },
    {
      date: "23 May",
      weight: 72,
      waist: 34,
      hip: 38,
      chest: 40,
      neck: 36,
      biceps: 30.5,
      forearm: 26.5,
      calf: 34,
      thighs: 54,
    },
  ]);
  // trend popup state
  const [trendModal, setTrendModal] = useState({
    open: false,
    key: "",
    label: "",
    color: "",
  });
  const [trendData, setTrendData] = useState([]);
  const [loadingTrend, setLoadingTrend] = useState(false);

  const fetchLatestVitals = async () => {
    if (!loggedUser?.patientId) return;
    try {
      const result = await VitalService.getVitalsRecentData(
        loggedUser.patientId,
      );
      if (result && result.data) {
        const we = result.data.find(
          (v) => v.vitalCode === "WE" && v.value !== 0 && v.value !== "0",
        )?.value;
        const wa = result.data.find(
          (v) => v.vitalCode === "WA" && v.value !== 0 && v.value !== "0",
        )?.value;
        const hp = result.data.find(
          (v) => v.vitalCode === "HP" && v.value !== 0 && v.value !== "0",
        )?.value;
        const ch = result.data.find(
          (v) => v.vitalCode === "CH" && v.value !== 0 && v.value !== "0",
        )?.value;
        const nk = result.data.find(
          (v) => v.vitalCode === "NK" && v.value !== 0 && v.value !== "0",
        )?.value;
        const bi = result.data.find(
          (v) => v.vitalCode === "BI" && v.value !== 0 && v.value !== "0",
        )?.value;
        const fa = result.data.find(
          (v) => v.vitalCode === "FA" && v.value !== 0 && v.value !== "0",
        )?.value;
        const cf = result.data.find(
          (v) => v.vitalCode === "CF" && v.value !== 0 && v.value !== "0",
        )?.value;
        const thg = result.data.find(
          (v) => v.vitalCode === "THG" && v.value !== 0 && v.value !== "0",
        )?.value;

        setVitals({
          weight: we ? we + " kg" : "-",
          waist: wa ? wa + " cm" : "-",
          hip: hp ? hp + " ratio" : "-",
          chest: ch ? ch + " cm" : "-",
          neck: nk ? nk + " cm" : "-",
          biceps: bi ? bi + " cm" : "-",
          forearm: fa ? fa + " cm" : "-",
          calf: cf ? cf + " cm" : "-",
          thighs: thg ? thg + " cm" : "-",
        });
      }
    } catch (e) {
      console.error("Error fetching recent vitals:", e);
    }
  };

  const handleSaveVitals = async () => {
    try {
      setSaving(true);
      let constructedDate = moment();
      if (tempVitals.recordedDate) {
        const dateStr = dayjs(tempVitals.recordedDate).format("YYYY-MM-DD");
        const timeStr = moment().format("HH:mm:ss");
        constructedDate = moment(
          `${dateStr} ${timeStr}`,
          "YYYY-MM-DD HH:mm:ss",
        );
      }
      const currentDate = constructedDate.utc().format("YYYY-MM-DD HH:mm:ss");
      const activePatient = JSON.parse(
        sessionStorage.getItem("patientData") || "{}",
      );
      const patientCode = loggedUser?.patientId || activePatient?.patientId;
      const patientName =
        `${activePatient?.salutation || ""}${activePatient?.firstName || ""} ${activePatient?.lastName || ""}`.trim();

      const payload = [];
      const vitalsMap = [
        { key: "weight", vitalCode: "WE", vitalName: "Weight", units: "Kg" },
        { key: "waist", vitalCode: "WA", vitalName: "Waist", units: "Cm" },
        { key: "hip", vitalCode: "HP", vitalName: "Hip Ratio", units: "Ratio" },
        { key: "chest", vitalCode: "CH", vitalName: "Chest", units: "Cm" },
        { key: "neck", vitalCode: "NK", vitalName: "Neck", units: "Cm" },
        { key: "biceps", vitalCode: "BI", vitalName: "Biceps", units: "Cm" },
        { key: "forearm", vitalCode: "FA", vitalName: "Forearm", units: "Cm" },
        { key: "calf", vitalCode: "CF", vitalName: "Calf", units: "Cm" },
        { key: "thighs", vitalCode: "THG", vitalName: "Thighs", units: "Cm" },
      ];

      vitalsMap.forEach((vital) => {
        const val = tempVitals[vital.key];
        if (val && !isNaN(parseFloat(val))) {
          payload.push({
            consultationId: null,
            createdBy: patientCode,
            createdDate: currentDate,
            createdName: patientName,
            modifiedBy: patientCode,
            modifiedDate: currentDate,
            patientId: patientCode,
            recordedDateTime: currentDate,
            status: 1,
            units: vital.units,
            value: parseFloat(val),
            vitalCode: vital.vitalCode,
            vitalName: vital.vitalName,
          });
        }
      });

      if (payload.length > 0) {
        await VitalService.AddVitals(payload);
        message.success("Vitals Added Successfully");
        fetchLatestVitals();
        setTempVitals({
          weight: "",
          waist: "",
          hip: "",
          chest: "",
          neck: "",
          biceps: "",
          forearm: "",
          calf: "",
          thighs: "",
          recordedDate: dayjs(),
        });
      }

      setIsAddVitalsModalOpen(false);
    } catch (error) {
      console.error("Error sending vitals info:", error);
      message.error("Failed to add vitals");
    } finally {
      setSaving(false);
    }
  };

  const getMealImageUrl = (item) => {
    if (!item) return null;
    if (item.actionImageName) {
      return `${process.env.REACT_APP_DIET_PLAN_URL}${item.actionImageName}`;
    } else if (item.actionImage) {
      return item.actionImage.startsWith("data:")
        ? item.actionImage
        : `data:image/${item.actionImageType || "jpeg"};base64,${item.actionImage}`;
    } else if (item.mealPhoto) {
      return item.mealPhoto;
    }
    return null;
  };

  const handleViewImageModal = (item) => {
    const url = getMealImageUrl(item);
    if (url) {
      setViewImageUrl(url);
      setIsViewImageModalOpen(true);
    } else {
      message.info("No photo proof available yet");
    }
  };

  const handleActivityChange = async (dayTitle, newStatus) => {
    if (!selectedDay) return;

    const updatedDetails = selectedDay.patientDietPlanDetails.map((item) => {
      if (item.dayTitle === dayTitle) {
        return {
          ...item,
          status: 1,
          activityStatus: newStatus,
          modifiedBy: loggedUser?.patientId,
        };
      }
      return item;
    });

    const updatedPlans = dietPlans.map((plan, idx) => {
      if (idx === selectedDayIndex) {
        return {
          ...plan,
          patientDietPlanDetails: updatedDetails,
        };
      }
      return plan;
    });
    setDietPlans(updatedPlans);

    try {
      const payload = {
        ...selectedDay,
        patientDietPlanDetails: updatedDetails,
        modifiedBy: loggedUser?.patientId,
      };

      await DietService.dietPlanUpdate(payload);
      message.success(`Activity status updated`);
      await fetchDietPlans(true);
    } catch (error) {
      console.error("Error updating activity status:", error);
      message.error("Failed to update activity status");
      fetchDietPlans();
    }
  };

  const formatTime12hr = (timeString) => {
    if (!timeString) return "";
    try {
      const parts = timeString.split(":");
      let hours = parseInt(parts[0], 10);
      const minutes = parts[1];

      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12;
      const paddedHours = hours.toString().padStart(2, "0");

      return `${paddedHours}:${minutes} ${ampm}`;
    } catch (e) {
      return timeString;
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    fetchDietPlans();
    fetchLatestVitals();
    return () => window.removeEventListener("resize", handleResize);
  }, [loggedUser]);

  useEffect(() => {
    const fetchTrendData = async () => {
      if (!trendModal.open || !trendModal.key) return;
      setLoadingTrend(true);
      setTrendData([]);
      try {
        const codeMap = {
          weight: "WE",
          waist: "WA",
          hip: "HP",
          chest: "CH",
          neck: "NK",
          biceps: "BI",
          forearm: "FA",
          calf: "CF",
          thighs: "THG",
        };
        const code = codeMap[trendModal.key];

        const fromDate = dayjs().subtract(30, "day").startOf("day");
        const toDate = dayjs().endOf("day");
        const rangePayload = {
          patientId: loggedUser?.patientId,
          vitalCode: [code],
          fromDate: fromDate.format("MM/DD/YYYY"),
          toDate: toDate.format("MM/DD/YYYY"),
          status: 1,
        };
        const result = await VitalService.getVitalForGraph(rangePayload);
        const allVitals = result?.data || [];
        const validVitals = allVitals.filter(
          (x) => x.value && x.value !== 0 && x.value !== "0",
        );

        validVitals.sort(
          (a, b) =>
            dayjs(a.recordedDateTime).valueOf() -
            dayjs(b.recordedDateTime).valueOf(),
        );

        const tmpData = validVitals.map((item) => ({
          date: dayjs(item.recordedDateTime).format("DD/MM/YYYY"),
          value: parseFloat(item.value),
        }));

        setTrendData(tmpData);
      } catch (e) {
        console.error("Error fetching trend data:", e);
      } finally {
        setLoadingTrend(false);
      }
    };
    fetchTrendData();
  }, [trendModal.open, trendModal.key, loggedUser]);

  const fetchDietPlans = async (silent = false) => {
    if (!loggedUser?.patientId) return;
    try {
      if (!silent) setLoading(true);
      const res = await DietService.getDietList({
        patientId: loggedUser.patientId,
      });
      if (res.data && Array.isArray(res.data)) {
        const filteredPlans = res.data.filter((plan) => {
          return (
            plan.paymentRequiredYN === "N" ||
            (plan.paymentRequiredYN === "Y" &&
              plan?.paidStatus?.toUpperCase() === "PAID")
          );
        });

        const sortedPlans = filteredPlans.map((plan) => {
          if (
            plan.patientDietPlanDetails &&
            Array.isArray(plan.patientDietPlanDetails)
          ) {
            const sortedDetails = [...plan.patientDietPlanDetails].sort(
              (a, b) => {
                const dayA = getDayNumber(a.dayTitle);
                const dayB = getDayNumber(b.dayTitle);
                if (dayA !== dayB) return dayA - dayB;
                return (a.sortOrder || 0) - (b.sortOrder || 0);
              },
            );
            return {
              ...plan,
              patientDietPlanDetails: sortedDetails,
            };
          }
          return plan;
        });

        // Sort the plans themselves by fromDate and toDate (descending)
        const finalSortedPlans = sortedPlans.sort((a, b) => {
          const startA = a.fromDate ? new Date(a.fromDate).getTime() : 0;
          const startB = b.fromDate ? new Date(b.fromDate).getTime() : 0;

          if (startB !== startA) {
            return startB - startA;
          }

          const endA = a.toDate ? new Date(a.toDate).getTime() : 0;
          const endB = b.toDate ? new Date(b.toDate).getTime() : 0;
          return endB - endA;
        });

        setDietPlans(finalSortedPlans);

        if (!silent && finalSortedPlans.length > 0) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const currentIndex = finalSortedPlans.findIndex((plan) => {
            const startDate = plan.fromDate ? new Date(plan.fromDate) : null;
            if (!startDate) return false;
            startDate.setHours(0, 0, 0, 0);

            let endDate = plan.toDate ? new Date(plan.toDate) : null;
            if (!endDate && plan.noOfDays) {
              endDate = new Date(startDate);
              endDate.setDate(
                startDate.getDate() + parseInt(plan.noOfDays) - 1,
              );
            }

            if (endDate) {
              endDate.setHours(23, 59, 59, 999);
              return today >= startDate && today <= endDate;
            }
            return today.getTime() === startDate.getTime();
          });

          if (currentIndex !== -1) {
            setSelectedDayIndex(currentIndex);
          } else {
            setSelectedDayIndex(0);
          }
        }
      } else {
        setDietPlans([]);
      }
    } catch (error) {
      console.error("Error fetching diet plans:", error);
      message.error("Failed to load diet plans");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (meal, newStatus) => {
    if (!selectedDay) return;

    // Optimistically update local state so the UI updates instantly
    if (newStatus === "taken") {
      openUploadModal(meal);
      return;
    }

    const updatedDetails = selectedDay.patientDietPlanDetails.map((item) => {
      if (item.id === meal.id) {
        return {
          ...item,
          status: 1,
          compliance: newStatus,
          actionStatus: "Y",
          actionImage: meal.actionImage || null,
          actionImageType: meal.actionImageType || null,
          modifiedBy: loggedUser?.patientId,
        };
      }
      return item;
    });

    const updatedPlans = dietPlans.map((plan, idx) => {
      if (idx === selectedDayIndex) {
        return {
          ...plan,
          patientDietPlanDetails: updatedDetails,
        };
      }
      return plan;
    });
    setDietPlans(updatedPlans);

    try {
      const payload = {
        ...selectedDay,
        patientDietPlanDetails: updatedDetails,
        modifiedBy: loggedUser?.patientId,
      };

      await DietService.dietPlanUpdate(payload);
      message.success(`Status updated successfully`);
      await fetchDietPlans(true);
    } catch (error) {
      console.error("Error updating status:", error);
      message.error("Failed to update status");
      // Rollback to sync state on error
      fetchDietPlans();
    }
  };

  const openUploadModal = (meal) => {
    setCurrentMeal(meal);
    let preview = null;
    if (meal.actionImage) {
      preview = meal.actionImage.startsWith("data:")
        ? meal.actionImage
        : `data:image/${meal.actionImageType || "jpeg"};base64,${meal.actionImage}`;
    } else {
      preview = meal.mealPhoto || null;
    }
    setUploadPreview(preview);
    setUploadFile(null);
    setIsCameraActive(false);
    setIsUploadModalOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
      setIsCameraActive(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const capturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      setUploadPreview(imageSrc);
      setIsCameraActive(false);
    }
  };

  const handleSaveUpload = async () => {
    if (!selectedDay || !currentMeal) return;
    if (!uploadPreview) {
      message.warning("Please select a file first");
      return;
    }
    try {
      setSaving(true);
      let base64Data = uploadPreview;
      let imageType = "jpeg";

      if (uploadPreview.startsWith("data:")) {
        const parts = uploadPreview.split(",");
        base64Data = parts[1];
        const match = parts[0].match(/data:image\/(.*?);base64/);
        if (match && match[1]) {
          imageType = match[1];
          if (imageType === "jpg") imageType = "jpeg";
        }
      }

      const updatedDetails = selectedDay.patientDietPlanDetails.map((item) => {
        if (item.id === currentMeal.id) {
          return {
            ...item,
            actionImage: base64Data,
            actionImageType: imageType,
            compliance: "taken",
            actionStatus: "Y",
            status: 1,
            modifiedBy: loggedUser?.patientId,
          };
        }
        return item;
      });

      const payload = {
        ...selectedDay,
        patientDietPlanDetails: updatedDetails,
        modifiedBy: loggedUser?.patientId,
      };

      await DietService.dietPlanUpdate(payload);
      const uploadedDetail = updatedDetails.find(
        (d) => d.id === currentMeal.id,
      );
      await notifyDietImageUpload({
        id: payload?.id,
        patientId: loggedUser?.patientId,
        patientName: payload.patientName,
        doctorId: payload.userId,
        consultationId: payload.consultationId,
        chartTitle: payload.chartTitle,
        meal: uploadedDetail?.meal,
        mealTime: uploadedDetail?.mealTime,
        dayTitle: uploadedDetail?.dayTitle,
        compliance: uploadedDetail?.compliance,
      });
      message.success("Photo and compliance uploaded successfully");
      setIsUploadModalOpen(false);
      await fetchDietPlans(true);
    } catch (error) {
      console.error("Error uploading photo:", error);
      message.error("Failed to upload photo");
    } finally {
      setSaving(false);
    }
  };

  const selectedDay = dietPlans[selectedDayIndex] || null;

  const daysArray = [];
  if (selectedDay?.patientDietPlanDetails) {
    const planStartDate = selectedDay.fromDate
      ? dayjs(selectedDay.fromDate)
      : null;

    selectedDay.patientDietPlanDetails.forEach((item) => {
      const dayName = item.dayTitle || "Day 1";
      let dayObj = daysArray.find((d) => d.dayTitle === dayName);
      if (!dayObj) {
        let displayDate = "";
        let dayOfWeek = "";

        if (planStartDate && planStartDate.isValid()) {
          const dayNum = getDayNumber(dayName);
          if (dayNum > 0) {
            const calculatedDate = planStartDate.add(dayNum - 1, "day");
            displayDate = calculatedDate.format("DD MMM YYYY");
            dayOfWeek = calculatedDate.format("dddd");
          }
        }

        dayObj = {
          dayTitle: dayName,
          meals: [],
          activity: item.activity || "",
          notes: item.notes || "",
          activityStatus: item.activityStatus || "",
          date: displayDate,
          dayOfWeek: dayOfWeek,
        };
        daysArray.push(dayObj);
      }
      dayObj.meals.push(item);
      if (item.activity && !dayObj.activity) {
        dayObj.activity = item.activity;
      }
      if (item.activityStatus && !dayObj.activityStatus) {
        dayObj.activityStatus = item.activityStatus;
      }
      if (item.notes && !dayObj.notes) {
        dayObj.notes = item.notes;
      }
    });
  }

  return (
    <Layout>
      <div className="w-[90%] sm:w-full mx-auto p-5">
        <div className="flex sm:flex-col items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-teal-900 font-opensans">
              Diet Chart Plan
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              View and manage your daily nutritional schedule
            </p>
          </div>

          <div className="flex items-center gap-4 sm:mt-4">
            <button
              onClick={() => setIsAddVitalsModalOpen(true)}
              className="bg-[#0CB8A1] text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-md hover:bg-[#089488] hover:-translate-y-0.5 transition-all shadow-emerald-100/50"
            >
              + Add
            </button>
            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
              Select Plan
            </span>
            <select
              value={selectedDayIndex}
              onChange={(e) => setSelectedDayIndex(Number(e.target.value))}
              className="border border-teal-100 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0CB8A1] bg-white shadow-sm font-medium text-teal-800 min-w-[200px]"
            >
              {dietPlans.length > 0 ? (
                dietPlans.map((dayItem, index) => (
                  <option key={index} value={index}>
                    {dayItem.chartTitle || `Plan ${index + 1}`}
                  </option>
                ))
              ) : (
                <option>No plans available</option>
              )}
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scrollwidth p-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-teal-50">
              <Spin size="large" />
              <p className="mt-4 text-teal-600 animate-pulse font-medium">
                Fetching your diet plan details...
              </p>
            </div>
          ) : !selectedDay ? (
            <div className="bg-white rounded-3xl border border-teal-50 shadow-sm p-16 text-center">
              <Empty
                description={
                  <span className="text-gray-400 font-medium">
                    No diet plan assigned yet.
                  </span>
                }
              />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-4 mb-6 p-4 bg-white/60 backdrop-blur-md border border-teal-100 rounded-[2rem] shadow-sm">
                {/* Weight Card */}
                <div className="bg-white px-5 py-3 rounded-xl shadow-md shadow-teal-900/5 border border-teal-50 min-w-[140px] flex-1 flex items-center gap-3 transition-transform hover:scale-105 relative">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-500 flex items-center justify-center shrink-0 shadow-sm border border-emerald-100">
                    <FaWeight className="text-lg" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                      Weight
                    </p>
                    <p className="text-base font-black text-teal-950 mt-0.5">
                      {vitals.weight || "-"}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setTrendModal({
                        open: true,
                        key: "weight",
                        label: "Weight",
                        color: "#10b981",
                      })
                    }
                    title="View Trend"
                    className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-500 flex items-center justify-center hover:bg-emerald-100 transition-colors shrink-0"
                  >
                    <FaChartLine className="text-xs" />
                  </button>
                </div>
                {/* Waist Card */}
                <div className="bg-white px-5 py-3 rounded-xl shadow-md shadow-teal-900/5 border border-teal-50 min-w-[140px] flex-1 flex items-center gap-3 transition-transform hover:scale-105">
                  <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-500 flex items-center justify-center shrink-0 shadow-sm border border-blue-100">
                    <FaTape className="text-lg" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                      Waist
                    </p>
                    <p className="text-base font-black text-teal-950 mt-0.5">
                      {vitals.waist || "-"}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setTrendModal({
                        open: true,
                        key: "waist",
                        label: "Waist",
                        color: "#3b82f6",
                      })
                    }
                    title="View Trend"
                    className="w-7 h-7 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center hover:bg-blue-100 transition-colors shrink-0"
                  >
                    <FaChartLine className="text-xs" />
                  </button>
                </div>
                {/* Hip Card */}
                <div className="bg-white px-5 py-3 rounded-xl shadow-md shadow-teal-900/5 border border-teal-50 min-w-[140px] flex-1 flex items-center gap-3 transition-transform hover:scale-105">
                  <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-500 flex items-center justify-center shrink-0 shadow-sm border border-indigo-100">
                    <FaChartPie className="text-lg" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                      Hip
                    </p>
                    <p className="text-base font-black text-teal-950 mt-0.5">
                      {vitals.hip || "-"}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setTrendModal({
                        open: true,
                        key: "hip",
                        label: "Hip",
                        color: "#6366f1",
                      })
                    }
                    title="View Trend"
                    className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-500 flex items-center justify-center hover:bg-indigo-100 transition-colors shrink-0"
                  >
                    <FaChartLine className="text-xs" />
                  </button>
                </div>
                {/* Chest Card */}
                <div className="bg-white px-5 py-3 rounded-xl shadow-md shadow-teal-900/5 border border-teal-50 min-w-[140px] flex-1 flex items-center gap-3 transition-transform hover:scale-105">
                  <div className="w-10 h-10 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center shrink-0 shadow-sm border border-rose-100">
                    <FaHeartbeat className="text-lg" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                      Chest
                    </p>
                    <p className="text-base font-black text-teal-950 mt-0.5">
                      {vitals.chest || "-"}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setTrendModal({
                        open: true,
                        key: "chest",
                        label: "Chest",
                        color: "#f43f5e",
                      })
                    }
                    title="View Trend"
                    className="w-7 h-7 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center hover:bg-rose-100 transition-colors shrink-0"
                  >
                    <FaChartLine className="text-xs" />
                  </button>
                </div>
                {/* Neck Card */}
                <div className="bg-white px-5 py-3 rounded-xl shadow-md shadow-teal-900/5 border border-teal-50 min-w-[140px] flex-1 flex items-center gap-3 transition-transform hover:scale-105">
                  <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center shrink-0 shadow-sm border border-amber-100">
                    <FaRuler className="text-lg" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                      Neck
                    </p>
                    <p className="text-base font-black text-teal-950 mt-0.5">
                      {vitals.neck || "-"}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setTrendModal({
                        open: true,
                        key: "neck",
                        label: "Neck",
                        color: "#f59e0b",
                      })
                    }
                    title="View Trend"
                    className="w-7 h-7 rounded-lg bg-amber-50 text-amber-500 flex items-center justify-center hover:bg-amber-100 transition-colors shrink-0"
                  >
                    <FaChartLine className="text-xs" />
                  </button>
                </div>
                {/* Biceps Card */}
                <div className="bg-white px-5 py-3 rounded-xl shadow-md shadow-teal-900/5 border border-teal-50 min-w-[140px] flex-1 flex items-center gap-3 transition-transform hover:scale-105">
                  <div className="w-10 h-10 rounded-full bg-purple-50 text-purple-500 flex items-center justify-center shrink-0 shadow-sm border border-purple-100">
                    <GiBiceps className="text-lg" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                      Biceps
                    </p>
                    <p className="text-base font-black text-teal-950 mt-0.5">
                      {vitals.biceps || "-"}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setTrendModal({
                        open: true,
                        key: "biceps",
                        label: "Biceps",
                        color: "#a855f7",
                      })
                    }
                    title="View Trend"
                    className="w-7 h-7 rounded-lg bg-purple-50 text-purple-500 flex items-center justify-center hover:bg-purple-100 transition-colors shrink-0"
                  >
                    <FaChartLine className="text-xs" />
                  </button>
                </div>
                {/* Forearm Card */}
                <div className="bg-white px-5 py-3 rounded-xl shadow-md shadow-teal-900/5 border border-teal-50 min-w-[140px] flex-1 flex items-center gap-3 transition-transform hover:scale-105">
                  <div className="w-10 h-10 rounded-full bg-cyan-50 text-cyan-500 flex items-center justify-center shrink-0 shadow-sm border border-cyan-100">
                    <GiForearm className="text-lg" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                      Forearm
                    </p>
                    <p className="text-base font-black text-teal-950 mt-0.5">
                      {vitals.forearm || "-"}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setTrendModal({
                        open: true,
                        key: "forearm",
                        label: "Forearm",
                        color: "#06b6d4",
                      })
                    }
                    title="View Trend"
                    className="w-7 h-7 rounded-lg bg-cyan-50 text-cyan-500 flex items-center justify-center hover:bg-cyan-100 transition-colors shrink-0"
                  >
                    <FaChartLine className="text-xs" />
                  </button>
                </div>
                {/* Calf Card */}
                <div className="bg-white px-5 py-3 rounded-xl shadow-md shadow-teal-900/5 border border-teal-50 min-w-[140px] flex-1 flex items-center gap-3 transition-transform hover:scale-105">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 shadow-sm border border-emerald-100">
                    <GiLeg className="text-lg" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                      Calf
                    </p>
                    <p className="text-base font-black text-teal-950 mt-0.5">
                      {vitals.calf || "-"}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setTrendModal({
                        open: true,
                        key: "calf",
                        label: "Calf",
                        color: "#059669",
                      })
                    }
                    title="View Trend"
                    className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center hover:bg-emerald-100 transition-colors shrink-0"
                  >
                    <FaChartLine className="text-xs" />
                  </button>
                </div>
                {/* Thighs Card */}
                <div className="bg-white px-5 py-3 rounded-xl shadow-md shadow-teal-900/5 border border-teal-50 min-w-[140px] flex-1 flex items-center gap-3 transition-transform hover:scale-105">
                  <div className="w-10 h-10 rounded-full bg-fuchsia-50 text-fuchsia-500 flex items-center justify-center shrink-0 shadow-sm border border-fuchsia-100">
                    <FaRunning className="text-lg" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                      Thighs
                    </p>
                    <p className="text-base font-black text-teal-950 mt-0.5">
                      {vitals.thighs || "-"}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setTrendModal({
                        open: true,
                        key: "thighs",
                        label: "Thighs",
                        color: "#d946ef",
                      })
                    }
                    title="View Trend"
                    className="w-7 h-7 rounded-lg bg-fuchsia-50 text-fuchsia-500 flex items-center justify-center hover:bg-fuchsia-100 transition-colors shrink-0"
                  >
                    <FaChartLine className="text-xs" />
                  </button>
                </div>
              </div>
              <div className="w-full bg-white rounded-[2rem] border border-gray-100 shadow-xl shadow-teal-900/5 overflow-hidden">
                <div className="flex items-center justify-between px-8 py-5 bg-teal-50/30 border-b border-teal-50">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#0CB8A1] rounded-2xl flex items-center justify-center text-white text-xl shadow-lg shadow-emerald-100">
                      📋
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-teal-900 leading-tight">
                        {selectedDay.chartTitle}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {selectedDay.fromDate
                          ? dayjs(selectedDay.fromDate).format("DD MMM YYYY")
                          : "N/A"}
                        {selectedDay.fromDate && selectedDay.noOfDays > 1 && (
                          <>
                            {" "}
                            -{" "}
                            {dayjs(selectedDay.fromDate)
                              .add(parseInt(selectedDay.noOfDays) - 1, "day")
                              .format("DD MMM YYYY")}
                          </>
                        )}{" "}
                        • {selectedDay.noOfDays} Day(s)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                        selectedDay.planStatus === "Active"
                          ? "bg-emerald-500 text-white shadow-lg shadow-emerald-100"
                          : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      {selectedDay.planStatus || "Planned"}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-8 p-8 bg-gray-50/30">
                  {daysArray.map((day, dIdx) => (
                    <div
                      key={dIdx}
                      className="bg-white rounded-[2rem] border border-teal-50/80 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-center justify-between px-6 py-4 bg-teal-50/30 border-b border-teal-50">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="px-4 py-1.5 bg-[#0CB8A1] text-white rounded-full text-xs font-black uppercase tracking-wider shadow-sm flex items-center gap-2">
                              {day.dayTitle}
                              {day.dayOfWeek && (
                                <span className="opacity-80 border-l border-white/30 pl-2">
                                  {day.dayOfWeek}
                                </span>
                              )}
                            </span>
                            {day.date && (
                              <span className="text-xs text-[#0CB8A1] font-bold bg-teal-50 px-3 py-1 rounded-full border border-teal-100">
                                {day.date}
                              </span>
                            )}
                            <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">
                              {day.meals.length} Meal(s)
                            </span>
                          </div>
                        </div>

                        {/* Meals List */}
                        <div className="p-6 space-y-4">
                          {day.meals.map((item, mIdx) => (
                            <div
                              key={mIdx}
                              className="p-4 bg-gray-50/40 hover:bg-teal-50/10 border border-gray-100 hover:border-teal-50 rounded-2xl transition-all flex flex-col md:flex-row md:items-center justify-between gap-4 group"
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white border border-teal-50 flex items-center justify-center text-teal-600 shadow-sm shrink-0 mt-0.5">
                                  <FaUtensils className="text-sm text-[#0CB8A1]" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="px-2.5 py-0.5 bg-[#0CB8A1]/10 text-[#0CB8A1] rounded-full text-[10px] font-black uppercase tracking-widest">
                                      {item.mealType}
                                    </span>
                                    <span className="text-xs font-bold text-gray-400 flex items-center gap-1">
                                      <FaClock className="text-[10px]" />
                                      {formatTime12hr(item.mealTime)}
                                    </span>
                                  </div>
                                  <p className="text-sm font-bold text-teal-950 mt-1 leading-snug">
                                    {item.meal}
                                  </p>
                                </div>
                              </div>

                              {/* Status Selector & Actions */}
                              <div className="flex items-center gap-2.5 self-end md:self-center">
                                <select
                                  value={item.compliance || ""}
                                  onChange={(e) =>
                                    handleStatusChange(item, e.target.value)
                                  }
                                  className="bg-white border border-teal-100 rounded-xl px-3 py-2 text-xs font-bold text-teal-800 outline-none focus:ring-2 focus:ring-[#0CB8A1]/20 cursor-pointer shadow-sm hover:border-[#0CB8A1] transition-all"
                                >
                                  <option value="">Select Status</option>
                                  <option value="taken">Taken</option>
                                  <option value="skipped">Skipped</option>
                                </select>

                                <div className="flex items-center gap-1.5">
                                  {getMealImageUrl(item) && (
                                    <button
                                      onClick={() => handleViewImageModal(item)}
                                      className="w-9 h-9 rounded-xl border border-teal-100 flex items-center justify-center transition-all duration-300 shadow-sm hover:scale-105 overflow-hidden"
                                      title="View Photo Proof"
                                    >
                                      <img
                                        src={getMealImageUrl(item)}
                                        alt="Proof Thumbnail"
                                        className="w-full h-full object-cover"
                                      />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => openUploadModal(item)}
                                    className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 shadow-sm ${
                                      item.actionImage ||
                                      item.mealPhoto ||
                                      item.actionImageName
                                        ? "bg-emerald-100 text-emerald-600 hover:bg-emerald-200"
                                        : "bg-[#0CB8A1] text-white hover:bg-[#089488] hover:scale-110 shadow-lg shadow-emerald-100"
                                    }`}
                                    title={
                                      item.actionImage ||
                                      item.mealPhoto ||
                                      item.actionImageName
                                        ? "View/Update Photo"
                                        : "Upload Photo"
                                    }
                                  >
                                    <FaUpload className="text-xs" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Activity & Notes Callouts */}
                      {(day.activity || day.notes) && (
                        <div className="px-6 pb-6 pt-2 border-t border-gray-50 bg-gray-50/20 space-y-3">
                          {day.activity && (
                            <div className="p-3 bg-blue-50/30 border border-blue-50 rounded-2xl flex items-center justify-between gap-4">
                              <div className="flex items-start gap-2.5 flex-1 min-w-0">
                                <div className="w-7 h-7 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0 mt-0.5">
                                  <FaRunning className="text-xs" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="text-[10px] font-black uppercase tracking-wider text-blue-500 block">
                                    Today's Activity
                                  </span>
                                  <p className="text-xs font-semibold text-blue-900 mt-0.5 break-words">
                                    {day.activity}
                                  </p>
                                </div>
                              </div>
                              <select
                                value={day.activityStatus || ""}
                                onChange={(e) =>
                                  handleActivityChange(
                                    day.dayTitle,
                                    e.target.value,
                                  )
                                }
                                className="bg-white border border-blue-100 rounded-xl px-3 py-2 text-xs font-bold text-blue-800 outline-none focus:ring-2 focus:ring-[#0CB8A1]/20 cursor-pointer shadow-sm hover:border-blue-300 transition-all shrink-0"
                              >
                                <option value="">Select Status</option>
                                <option value="done">Done</option>
                                <option value="not done">Not Done</option>
                              </select>
                            </div>
                          )}

                          {day.notes && (
                            <div className="p-3 bg-amber-50/30 border border-amber-50 rounded-2xl flex items-start gap-2.5">
                              <div className="w-7 h-7 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600 shrink-0 mt-0.5">
                                <FaClipboardList className="text-xs" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="text-[10px] font-black uppercase tracking-wider text-amber-500 block">
                                  Diet Notes & Instructions
                                </span>
                                <p className="text-xs font-semibold text-amber-900 mt-0.5 break-words">
                                  {day.notes}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {isUploadModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-teal-900/40 backdrop-blur-md p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[2rem] w-full max-w-md shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 duration-500 border border-teal-50">
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      Proof of Meal
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Upload a photo of your{" "}
                      {currentMeal?.mealType?.toLowerCase()}
                    </p>
                  </div>
                  <button
                    onClick={() => !saving && setIsUploadModalOpen(false)}
                    disabled={saving}
                    className="w-10 h-10 rounded-full bg-gray-50 text-gray-400 hover:text-gray-600 flex items-center justify-center transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaTimes />
                  </button>
                </div>

                <div className="relative aspect-square w-full bg-teal-50/50 rounded-3xl border-2 border-dashed border-teal-200 flex flex-col items-center justify-center overflow-hidden mb-8 group transition-all hover:border-emerald-400">
                  {isCameraActive ? (
                    <div className="relative w-full h-full">
                      <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        videoConstraints={videoConstraints}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
                        <button
                          onClick={capturePhoto}
                          className="bg-emerald-500 text-white p-4 rounded-full shadow-lg hover:bg-emerald-600 transition-transform active:scale-95"
                        >
                          <FaCamera className="text-xl" />
                        </button>
                        <button
                          onClick={() => setIsCameraActive(false)}
                          className="bg-red-500 text-white p-4 rounded-full shadow-lg hover:bg-red-600 transition-transform active:scale-95"
                        >
                          <FaTimes className="text-xl" />
                        </button>
                      </div>
                    </div>
                  ) : uploadPreview ? (
                    <div className="relative w-full h-full group">
                      <img
                        src={uploadPreview}
                        alt="Meal Preview"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white font-medium bg-black/40 backdrop-blur-md px-4 py-2 rounded-full text-xs">
                          Click Change to replace
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center p-6">
                      <div className="w-20 h-20 bg-white rounded-2xl shadow-sm border border-teal-100 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                        <FaUpload className="text-3xl text-[#0CB8A1]" />
                      </div>
                      <p className="text-sm font-semibold text-teal-800">
                        Choose a photo
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        JPG, PNG or WEBP (Max 5MB)
                      </p>
                    </div>
                  )}
                </div>

                <input
                  type="file"
                  id="meal-photo-upload"
                  className="hidden"
                  onChange={handleFileChange}
                  accept="image/*"
                  disabled={saving}
                />

                <div className="flex flex-col gap-4">
                  <div className="flex gap-4">
                    <label
                      htmlFor={
                        saving || isCameraActive ? "" : "meal-photo-upload"
                      }
                      className={`flex-1 py-4 rounded-2xl flex items-center justify-center gap-2 cursor-pointer transition-all font-bold text-sm ${
                        saving || isCameraActive
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : uploadPreview
                            ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            : "bg-emerald-50 text-[#0CB8A1] border-2 border-emerald-100 hover:bg-emerald-100"
                      }`}
                    >
                      <FaUpload /> {uploadPreview ? "Gallery" : "Upload Image"}
                    </label>

                    <button
                      onClick={() => setIsCameraActive(true)}
                      disabled={saving}
                      className={`flex-1 py-4 rounded-2xl flex items-center justify-center gap-2 transition-all font-bold text-sm ${
                        saving || isCameraActive
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-teal-50 text-teal-600 border-2 border-teal-100 hover:bg-teal-100"
                      }`}
                    >
                      <FaCamera /> Take Photo
                    </button>
                  </div>

                  <div className="flex gap-4 mt-2">
                    <button
                      onClick={() => !saving && setIsUploadModalOpen(false)}
                      disabled={saving}
                      className="flex-1 py-4 text-gray-500 font-bold text-sm hover:text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveUpload}
                      disabled={!uploadPreview || saving}
                      className={`flex-[2] py-4 rounded-2xl font-bold text-sm transition-all shadow-xl ${
                        uploadPreview && !saving
                          ? "bg-[#0CB8A1] hover:bg-[#089488] text-white shadow-emerald-200 hover:-translate-y-1"
                          : "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none"
                      }`}
                    >
                      {saving ? "Saving..." : "Confirm & Save Photo"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isAddVitalsModalOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center bg-teal-900/40 backdrop-blur-md p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[2rem] w-full max-w-lg shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 duration-500 border border-teal-50 p-6 sm:p-8 max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-teal-900">
                    Add Details
                  </h3>
                </div>
                <button
                  onClick={() => setIsAddVitalsModalOpen(false)}
                  className="w-10 h-10 rounded-full bg-gray-50 text-gray-400 hover:text-gray-600 flex items-center justify-center transition-colors hover:bg-gray-100"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="overflow-y-auto pr-1 flex-1 py-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative w-full sm:col-span-2">
                    <span className="absolute -top-2 text-xs font-semibold z-10 left-3 bg-white px-1 text-gray-500">
                      Date
                    </span>
                    <DatePicker
                      value={tempVitals.recordedDate}
                      onChange={(date) =>
                        setTempVitals({
                          ...tempVitals,
                          recordedDate: date || dayjs(),
                        })
                      }
                      allowClear={false}
                      format="YYYY-MM-DD"
                      className="w-full py-2.5 px-3 border border-gray-300 rounded-md focus:outline-none text-xs font-medium font-sans text-[#232323] focus:border-[#189FAA] bg-[#F2FFFB]"
                    />
                  </div>
                  <div className="relative w-full">
                    <span className="absolute -top-2 text-xs font-semibold z-10 left-3 bg-white px-1 text-gray-500">
                      Weight (Kg)
                    </span>
                    <input
                      type="text"
                      value={tempVitals.weight}
                      onChange={(e) =>
                        setTempVitals({ ...tempVitals, weight: e.target.value })
                      }
                      className="w-full py-2.5 px-3 border border-gray-300 rounded-md focus:outline-none text-xs font-medium font-sans text-[#232323] focus:border-[#189FAA] bg-[#F2FFFB]"
                    />
                  </div>
                  <div className="relative w-full">
                    <span className="absolute -top-2 text-xs font-semibold z-10 left-3 bg-white px-1 text-gray-500">
                      Waist (Cm)
                    </span>
                    <input
                      type="text"
                      value={tempVitals.waist}
                      onChange={(e) =>
                        setTempVitals({ ...tempVitals, waist: e.target.value })
                      }
                      className="w-full py-2.5 px-3 border border-gray-300 rounded-md focus:outline-none text-xs font-medium font-sans text-[#232323] focus:border-[#189FAA] bg-[#F2FFFB]"
                    />
                  </div>
                  <div className="relative w-full">
                    <span className="absolute -top-2 text-xs font-semibold z-10 left-3 bg-white px-1 text-gray-500">
                      Hip (Ratio)
                    </span>
                    <input
                      type="text"
                      value={tempVitals.hip}
                      onChange={(e) =>
                        setTempVitals({ ...tempVitals, hip: e.target.value })
                      }
                      className="w-full py-2.5 px-3 border border-gray-300 rounded-md focus:outline-none text-xs font-medium font-sans text-[#232323] focus:border-[#189FAA] bg-[#F2FFFB]"
                    />
                  </div>
                  <div className="relative w-full">
                    <span className="absolute -top-2 text-xs font-semibold z-10 left-3 bg-white px-1 text-gray-500">
                      Chest (Cm)
                    </span>
                    <input
                      type="text"
                      value={tempVitals.chest}
                      onChange={(e) =>
                        setTempVitals({ ...tempVitals, chest: e.target.value })
                      }
                      className="w-full py-2.5 px-3 border border-gray-300 rounded-md focus:outline-none text-xs font-medium font-sans text-[#232323] focus:border-[#189FAA] bg-[#F2FFFB]"
                    />
                  </div>
                  <div className="relative w-full">
                    <span className="absolute -top-2 text-xs font-semibold z-10 left-3 bg-white px-1 text-gray-500">
                      Neck (Cm)
                    </span>
                    <input
                      type="text"
                      value={tempVitals.neck}
                      onChange={(e) =>
                        setTempVitals({ ...tempVitals, neck: e.target.value })
                      }
                      className="w-full py-2.5 px-3 border border-gray-300 rounded-md focus:outline-none text-xs font-medium font-sans text-[#232323] focus:border-[#189FAA] bg-[#F2FFFB]"
                    />
                  </div>
                  <div className="relative w-full">
                    <span className="absolute -top-2 text-xs font-semibold z-10 left-3 bg-white px-1 text-gray-500">
                      Biceps (Cm)
                    </span>
                    <input
                      type="text"
                      value={tempVitals.biceps}
                      onChange={(e) =>
                        setTempVitals({ ...tempVitals, biceps: e.target.value })
                      }
                      className="w-full py-2.5 px-3 border border-gray-300 rounded-md focus:outline-none text-xs font-medium font-sans text-[#232323] focus:border-[#189FAA] bg-[#F2FFFB]"
                    />
                  </div>
                  <div className="relative w-full">
                    <span className="absolute -top-2 text-xs font-semibold z-10 left-3 bg-white px-1 text-gray-500">
                      Forearm (Cm)
                    </span>
                    <input
                      type="text"
                      value={tempVitals.forearm}
                      onChange={(e) =>
                        setTempVitals({
                          ...tempVitals,
                          forearm: e.target.value,
                        })
                      }
                      className="w-full py-2.5 px-3 border border-gray-300 rounded-md focus:outline-none text-xs font-medium font-sans text-[#232323] focus:border-[#189FAA] bg-[#F2FFFB]"
                    />
                  </div>
                  <div className="relative w-full">
                    <span className="absolute -top-2 text-xs font-semibold z-10 left-3 bg-white px-1 text-gray-500">
                      Calf (Cm)
                    </span>
                    <input
                      type="text"
                      value={tempVitals.calf}
                      onChange={(e) =>
                        setTempVitals({ ...tempVitals, calf: e.target.value })
                      }
                      className="w-full py-2.5 px-3 border border-gray-300 rounded-md focus:outline-none text-xs font-medium font-sans text-[#232323] focus:border-[#189FAA] bg-[#F2FFFB]"
                    />
                  </div>
                  <div className="relative w-full sm:col-span-2">
                    <span className="absolute -top-2 text-xs font-semibold z-10 left-3 bg-white px-1 text-gray-500">
                      Thighs (Cm)
                    </span>
                    <input
                      type="text"
                      value={tempVitals.thighs}
                      onChange={(e) =>
                        setTempVitals({ ...tempVitals, thighs: e.target.value })
                      }
                      className="w-full py-2.5 px-3 border border-gray-300 rounded-md focus:outline-none text-xs font-medium font-sans text-[#232323] focus:border-[#189FAA] bg-[#F2FFFB]"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-4 pt-2">
                <button
                  onClick={() => setIsAddVitalsModalOpen(false)}
                  className="flex-1 py-3 text-gray-500 font-bold text-sm hover:text-gray-700 transition-colors bg-gray-50 hover:bg-gray-100 rounded-2xl"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveVitals}
                  className="flex-1 py-3 bg-[#0CB8A1] hover:bg-[#089488] text-white font-bold text-sm rounded-2xl transition-all shadow-xl shadow-emerald-100 hover:-translate-y-0.5"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Trend Graph Popup */}
        {trendModal.open &&
          (() => {
            const color = trendModal.color;
            const gradId = `trendGrad_${trendModal.key}`;
            return (
              <div className="fixed inset-0 z-[130] flex items-center justify-center bg-teal-900/40 backdrop-blur-md p-4 animate-in fade-in duration-300">
                <div className="bg-white rounded-[2rem] w-full max-w-lg shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 duration-500 border border-teal-50">
                  <div className="p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
                          style={{ background: `${color}20` }}
                        >
                          <FaChartLine
                            style={{ color }}
                            className="text-base"
                          />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-teal-900">
                            {trendModal.label}
                          </h3>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setTrendModal({
                            open: false,
                            key: "",
                            label: "",
                            color: "",
                          })
                        }
                        className="w-10 h-10 rounded-full bg-gray-50 text-gray-400 hover:text-gray-600 flex items-center justify-center transition-colors hover:bg-gray-100"
                      >
                        <FaTimes />
                      </button>
                    </div>

                    {loadingTrend ? (
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <Spin size="large" />
                        <p className="mt-4 text-teal-600 font-medium">
                          Fetching trend data...
                        </p>
                      </div>
                    ) : trendData.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mb-4">
                          <FaChartLine className="text-3xl text-gray-300" />
                        </div>
                        <p className="font-bold text-gray-400 text-sm">
                          No trend data yet
                        </p>
                        <p className="text-xs text-gray-300 mt-1">
                          Save measurements to see a graph
                        </p>
                      </div>
                    ) : (
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart
                            data={trendData}
                            margin={{
                              top: 10,
                              right: 10,
                              left: -20,
                              bottom: 0,
                            }}
                          >
                            <defs>
                              <linearGradient
                                id={gradId}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="5%"
                                  stopColor={color}
                                  stopOpacity={0.3}
                                />
                                <stop
                                  offset="95%"
                                  stopColor={color}
                                  stopOpacity={0}
                                />
                              </linearGradient>
                            </defs>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#f0f0f0"
                            />
                            <XAxis
                              dataKey="date"
                              axisLine={false}
                              tickLine={false}
                              tick={{
                                fill: "#6b7280",
                                fontSize: 10,
                                fontWeight: 500,
                              }}
                            />
                            <YAxis
                              axisLine={false}
                              tickLine={false}
                              tick={{ fill: "#6b7280", fontSize: 10 }}
                            />
                            <Tooltip
                              contentStyle={{
                                background: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: 600,
                                boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
                              }}
                              labelStyle={{ color: "#374151", fontWeight: 700 }}
                              itemStyle={{ color }}
                              formatter={(val) => [val, trendModal.label]}
                            />
                            <Area
                              type="monotone"
                              dataKey="value"
                              stroke={color}
                              strokeWidth={2.5}
                              fill={`url(#${gradId})`}
                              dot={{
                                r: 5,
                                fill: color,
                                stroke: "#fff",
                                strokeWidth: 2,
                              }}
                              activeDot={{
                                r: 7,
                                fill: color,
                                stroke: "#fff",
                                strokeWidth: 2,
                              }}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

        {isViewImageModalOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-teal-900/50 backdrop-blur-md p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[2rem] w-full max-w-md shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 duration-500 border border-teal-50">
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-teal-900">
                      Meal Photo Proof
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Uploaded proof of meal completion
                    </p>
                  </div>
                  <button
                    onClick={() => setIsViewImageModalOpen(false)}
                    className="w-10 h-10 rounded-full bg-gray-50 text-gray-400 hover:text-gray-600 flex items-center justify-center transition-colors hover:bg-gray-100"
                  >
                    <FaTimes />
                  </button>
                </div>

                <div className="relative aspect-square w-full bg-teal-50/20 rounded-3xl border border-teal-100 flex items-center justify-center overflow-hidden mb-6 shadow-inner">
                  <img
                    src={viewImageUrl}
                    alt="Meal Proof"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => setIsViewImageModalOpen(false)}
                    className="w-full py-4 bg-[#0CB8A1] hover:bg-[#089488] text-white font-bold text-sm rounded-2xl transition-all shadow-xl shadow-emerald-100 hover:-translate-y-0.5"
                  >
                    Close Preview
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default DietPlan;
